import { useState } from "react";
import Icon from "../icons/Icon";
import Modal from "../modals/Modal";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useToast } from "../../contexts/ToastContext";
import { countAccessLevels } from "../../config/matrix";

const functions = getFunctions(undefined, "asia-southeast1");
const deleteCustomRoleFn = httpsCallable(functions, "deleteCustomRole");

/**
 * DeleteRoleModal — Confirm-only destructive confirmation per PRD §5.5.
 *
 * The Delete button on the Roles list page is disabled when a role has
 * assigned users, so this modal is normally only opened for empty roles.
 * The CF can still race-fail with USERS_ASSIGNED if a user gets assigned
 * between the client snapshot and the CF call — in that case we surface
 * a toast and close, since the Roles page filter is the right place to
 * handle reassignment.
 */
const DeleteRoleModal = ({ role, onClose, onDeleted }) => {
  const toast = useToast();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await deleteCustomRoleFn({ roleId: role.id });
      toast.success(`Role "${role.name}" deleted.`);
      onDeleted?.(role.id);
    } catch (err) {
      console.error("Delete role failed:", err);
      const code = err?.details?.code;
      if (code === "USERS_ASSIGNED") {
        const count = err.details?.users?.length || 0;
        toast.error(
          `This role now has ${count} assigned user${count === 1 ? "" : "s"}. Reassign them via the Team page, then try again.`,
        );
        onClose();
      } else {
        toast.error(err?.message || "Failed to delete role.");
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} busy={deleting} labelledBy="delete-confirm-title" className="role-modal">
        <div className="role-modal-header">
          <h2 id="delete-confirm-title" className="role-modal-title">
            <Icon name="trash-2" size={16} aria-hidden="true" /> Delete &ldquo;{role.name}&rdquo;?
          </h2>
        </div>
        <div className="role-modal-body">
          <div className="role-modal-banner role-modal-banner-danger">
            This will permanently delete the role. This cannot be undone.
          </div>
          <div className="role-modal-meta">
            {countAccessLevels(role.permissions)} access level
            {countAccessLevels(role.permissions) === 1 ? "" : "s"}
            {role.description ? ` · ${role.description}` : ""}
          </div>
        </div>
        <div className="role-modal-footer">
          <button
            type="button"
            className="settings-btn settings-btn-secondary"
            onClick={onClose}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="settings-btn settings-btn-danger"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete Role"}
          </button>
        </div>
    </Modal>
  );
};

export default DeleteRoleModal;